import type { NormalizedBBox } from 'features/knowledgeDocumentViewer/types/ocrViewer'

// URL codec for the `selectedRegions` param: `;`-separated normalized
// `"x0,y0,x1,y1"` page rects. The coordinate twin of the legacy `selectedBoxes`
// box-id param; fim `Reference.boxes` emits the same quads `bbox:`-prefixed.

const REGION_SEPARATOR = ';'
const FIM_COORD_PREFIX = 'bbox:'
const COORD_DECIMALS = 5 // matches fim-service's format_box

const clamp01 = (v: number): number => Math.min(1, Math.max(0, v))

const formatCoord = (v: number): string =>
  String(parseFloat(clamp01(v).toFixed(COORD_DECIMALS)))

export function parseRegionString(value: string): NormalizedBBox | null {
  let body = value.trim()
  if (body.startsWith(FIM_COORD_PREFIX)) {
    body = body.slice(FIM_COORD_PREFIX.length)
  }
  const parts = body.split(',')
  if (parts.length !== 4) return null
  const nums = parts.map((part) => {
    const trimmed = part.trim()
    // Number('') === 0 — an empty component must not silently become a coord.
    return trimmed === '' ? NaN : Number(trimmed)
  })
  if (!nums.every((n) => Number.isFinite(n))) return null
  const [x0, y0, x1, y1] = nums.map(clamp01)
  if (x1 <= x0 || y1 <= y0) return null
  return { left: x0, top: y0, width: x1 - x0, height: y1 - y0 }
}

export function parseSelectedRegionsParam(
  raw: string | null | undefined
): NormalizedBBox[] {
  if (!raw) return []
  return raw
    .split(REGION_SEPARATOR)
    .map(parseRegionString)
    .filter((region): region is NormalizedBBox => region !== null)
}

export function serializeSelectedRegionsParam(
  regions: readonly NormalizedBBox[]
): string | undefined {
  const parts = regions
    .filter((r) => r.width > 0 && r.height > 0)
    .map((r) =>
      [r.left, r.top, r.left + r.width, r.top + r.height]
        .map(formatCoord)
        .join(',')
    )
  return parts.length > 0 ? parts.join(REGION_SEPARATOR) : undefined
}

export function regionsFromBoxStrings(
  boxes: readonly string[] | null | undefined
): NormalizedBBox[] {
  return (boxes ?? [])
    .map(parseRegionString)
    .filter((region): region is NormalizedBBox => region !== null)
}

export function selectedRegionsParamFromBoxStrings(
  boxes: readonly string[] | null | undefined
): string | undefined {
  return serializeSelectedRegionsParam(regionsFromBoxStrings(boxes))
}

export function isCoordBoxString(value: string): boolean {
  return parseRegionString(value) !== null
}
